<template>
    <div class="drawer-backdrop" @click.self="closeDrawer">
        <transition name="slide-in-right">
            <div class="block-drawer">
                <!-- Header -->
                <div class="drawer-header">
                    <div class="header-content">
                        <h2 class="block-name">Consolidate billing</h2>
                        <p class="block-meta">
                            Line up every auto-renewing license on one shared renewal date.
                        </p>
                    </div>
                    <button type="button" class="close-btn" :disabled="scheduling" @click="closeDrawer">
                        <PhX :size="20" />
                    </button>
                </div>

                <!-- Loading -->
                <div v-if="isLoading && !preview" class="drawer-loading">
                    <PrimeSkeleton v-for="n in 4" :key="n" width="100%" height="56px" class="mb-3" />
                </div>

                <!-- Scheduled confirmation -->
                <div v-else-if="scheduledSummary" class="drawer-body">
                    <div class="scheduled-confirm">
                        <PhCheckCircle :size="40" weight="fill" class="scheduled-confirm__icon" />
                        <h3 class="scheduled-confirm__title">Consolidation scheduled</h3>

                        <!--
                            What actually happened to the money (PB-074). The invoice's STATUS
                            decides the words: `paid` is the only one that means the card was
                            charged, and anything else means the cost is owed — a charge that
                            did not land also leaves the invoice open. With no invoice in the
                            response at all the pre-PB-074 line stands, because that API charges
                            nothing at scheduling.

                            NOT-PAID IS NOT THE SAME AS NOT-ATTEMPTED, so the line says only what
                            the status proves. An unpaid consolidation invoice reaches this screen
                            on three paths — no card on file, a declined charge, and a charge still
                            settling, which Invoice::charge() leaves OPEN with a live
                            `pending_payment_intent_id` — and the response carries nothing that
                            tells them apart. "Nothing was charged to a payment method" is
                            therefore a claim this screen cannot make; the settling charge can
                            still capture. For the same reason it does not send the administrator
                            off to pay: the Invoices tab is where the invoice and any attempt on
                            it are, and pressing Pay at a live intent is not what that trip is for.
                        -->
                        <p v-if="scheduledInvoice && chargedNow" class="scheduled-confirm__line scheduled-confirm__money">
                            {{ formatCurrency(scheduledInvoice.amount) }} was charged to your payment method
                            on invoice {{ scheduledInvoice.id }}.
                        </p>
                        <!--
                            Credit that covers the alignment outright settles it without a charge:
                            app-api closes the invoice instead of charging it
                            (Invoice::closeFullyCreditCovered), so it arrives CLOSED at zero. Read
                            as "not paid" it said an invoice of $0.00 was owed and sent the
                            administrator to go and pay it, which is wrong three times over — the
                            cost is settled, consolidation is never $0, and a closed invoice is not
                            payable. NO FIGURE on this line: `amount` is the nil remainder after
                            the credit draw, and the response says nothing about how much credit
                            was spent, so there is no honest number to print.
                        -->
                        <p v-else-if="scheduledInvoice && creditSettled" class="scheduled-confirm__line scheduled-confirm__money">
                            Account credit covered this alignment in full — invoice
                            {{ scheduledInvoice.id }} is settled.
                        </p>
                        <p v-else-if="scheduledInvoice" class="scheduled-confirm__line scheduled-confirm__money">
                            {{ formatCurrency(scheduledInvoice.amount) }} was invoiced to your account on
                            invoice {{ scheduledInvoice.id }}<template v-if="scheduledInvoice.due_on">, due
                            {{ formatBillingDate(scheduledInvoice.due_on) }}</template>. It has not been
                            collected yet — open it in the Invoices tab.
                        </p>
                        <p v-else class="scheduled-confirm__line">No amount was charged today.</p>

                        <p v-if="scheduledSummary.first_expected_invoice" class="scheduled-confirm__line">
                            {{ scheduledInvoice ? 'A further invoice' : 'First invoice' }}:
                            {{ formatCurrency(scheduledSummary.first_expected_invoice.estimated_amount) }}
                            around {{ formatBillingDate(scheduledSummary.first_expected_invoice.date) }}.
                        </p>
                        <p class="scheduled-confirm__line">
                            All {{ scheduledSummary.total_seats }} licenses then renew together on
                            {{ formatBillingDate(scheduledSummary.shared_date) }}.
                        </p>
                        <PrimeButton label="Done" class="mt-4" @click="closeDrawer" />
                    </div>
                </div>

                <!-- Body -->
                <div v-else-if="preview" class="drawer-body">
                    <!-- Shared date picker -->
                    <section class="drawer-section">
                        <label class="field-label" for="shared-date">Shared renewal date</label>
                        <input
                            id="shared-date"
                            type="date"
                            class="date-input"
                            :min="earliestSharedDate ?? undefined"
                            :value="sharedDate"
                            :disabled="isLoading || scheduling"
                            @change="onSharedDateChange"
                        />
                        <p class="field-hint">
                            The earliest you can choose is your furthest-out current renewal,
                            {{ formatBillingDate(earliestSharedDate) || 'N/A' }}.
                        </p>
                    </section>

                    <!--
                        Per-block estimate schedule, no total above the list (PB-073) — the
                        total the administrator is committing to sits in the footer, beside the
                        press, because that is what they are being charged (PB-074). Blocks,
                        not licenses: a block is what the financial page lists and what the
                        administrator clicked through from, so the row names the block id
                        the page printed. Every figure here is server-computed.
                    -->
                    <section class="drawer-section">
                        <div class="section-head">
                            <h3 class="section-title">Estimated schedule</h3>
                            <span class="section-count">{{ preview.blocks.length }} {{ preview.blocks.length === 1 ? 'block' : 'blocks' }}</span>
                        </div>

                        <p class="section-lede">
                            Each block below keeps its own renewal date until it is extended to the
                            shared date, {{ formatBillingDate(unifiedRenewalDate) || 'N/A' }}.
                            Costs are estimates until each invoice is raised.
                        </p>

                        <ul class="seat-list">
                            <li v-for="block in preview.blocks" :key="block.block_id" class="seat-row">
                                <div class="seat-meta">
                                    <!-- Both fields, because the slug alone is not enough for a
                                         partner's own SKU: it is not in the tier map, so the badge
                                         humanises the slug (or reads "No license" when there is
                                         none) while this payload carries the authoritative
                                         catalogue name. `licenseTier` prefers that name for a slug
                                         it does not know. -->
                                    <LicenseBadge :license="{ slug: block.license_slug, name: block.license_name }" size="sm" />
                                    <span class="block-id">{{ block.block_id }}</span>
                                    <span class="seat-detail">
                                        {{ block.license_count }} {{ block.license_count === 1 ? 'license' : 'licenses' }}
                                        · renews {{ formatBillingDate(block.current_renewal_date) }}
                                    </span>
                                </div>
                                <div class="seat-amount">
                                    <span v-if="block.exempt" class="seat-exempt">
                                        Already renews on the shared date — no earlier invoice
                                    </span>
                                    <span v-else class="seat-est">
                                        {{ formatCurrency(block.estimated_extension_amount) }}
                                        <span class="seat-est__label">est. extension</span>
                                    </span>
                                </div>
                            </li>

                            <!--
                                The schedule closes with the renewal that follows the shared date,
                                stated as the rule rather than a day. The preview sends each
                                license's CURRENT term, and a license with a plan or term change
                                scheduled renews on its NEW term (the server prices the extension
                                and the renewal from effectiveTerm()), so a day counted from
                                `term` would be wrong for exactly those licenses — and nothing on
                                the wire says which ones they are. Follow-up PB-077-FU: expose the
                                effective term on the consolidation preview, then name the day.
                            -->
                            <li v-if="unifiedRenewalDate" class="seat-row following-renewal">
                                <div class="seat-meta">
                                    <span class="seat-person">Then each license renews again</span>
                                    <span class="seat-detail">One full term after {{ formatBillingDate(unifiedRenewalDate) }}, on its own term</span>
                                </div>
                            </li>
                        </ul>

                        <p v-if="hasMixedTerms" class="drawer-help">
                            Your licenses have different term lengths. Consolidation aligns only the next
                            renewal date — each license keeps its own term afterward.
                        </p>
                    </section>
                </div>

                <!-- Empty -->
                <div v-else class="drawer-empty">
                    <p>There are no eligible licenses to consolidate.</p>
                </div>

                <!-- Footer: disclosure + payment warning + confirm -->
                <div v-if="preview && !scheduledSummary" class="drawer-footer">
                    <!--
                        A consolidation that would cost nothing is refused by app-api with a
                        422, so this says so instead of offering a press that can only fail.
                        The preview does not refuse — it returns the exempt blocks at 0 — so
                        the refusal is read off those figures.
                    -->
                    <div v-if="nothingToCharge" class="payment-warning">
                        <PhWarning :size="18" class="payment-warning__icon" />
                        <span>
                            Every block above already renews on {{ formatBillingDate(unifiedRenewalDate) }}, so this
                            consolidation would cost nothing — and a consolidation always has a cost. Choose a later
                            shared renewal date to consolidate these licenses.
                        </span>
                    </div>

                    <!--
                        What the alignment is estimated to cost, stated before the press (PB-074).
                        The figures are the server's per-block estimates, summed.

                        NAMED AS AN ESTIMATE, and it has to be. Nothing binds this sum to the
                        invoice: `schedule` takes a shared date and nothing else, and the server
                        prices every license again at that moment. Two of the gaps are structural
                        rather than racy — ReconcileConsolidationInvoicesAction runs first and can
                        put a license's extension on an unpaid renewal invoice that already exists,
                        which leaves it off this one; and a license whose plan row has been deleted
                        is quoted here from its last agreed price but dropped from the invoice by
                        chargeableBlocks. The eligible set moves too, since it is re-resolved from
                        the company's own licenses under a lock. So this screen may not promise the
                        figure: the invoice is the authority, and the confirmation below reads it.

                        AND IT STATES NO COLLECTION EVENT, because it cannot know there will be
                        one. Charging at schedule time is PB-074 behaviour, and the preview carries
                        no marker for which API half is deployed — `blocks` is PB-073 and ships
                        ahead of it, so even its presence proves nothing. Against the server
                        running today, scheduling charges nothing and returns no invoice, which is
                        the case the confirmation's "No amount was charged today" exists for; a
                        footer that had just promised a charge today would be contradicted by it
                        one press later. So the claim is dropped rather than guessed: the cost is
                        what this screen knows, and the invoice — if one is raised — is what
                        reports the rest. The card-on-file note below is conditional for the same
                        reason.

                        Gated on a cost actually being quoted, not just on `blocks` being
                        non-empty: a preview that predates the rollup degrades to no blocks at all,
                        and `nothingToCharge` deliberately ignores that case, which left this
                        quoting "$0.00" on a surface where a consolidation is never free.
                    -->
                    <div v-else-if="estimatedTotal > 0" class="disclosure">
                        <PhInfo :size="18" class="disclosure__icon" />
                        <div class="disclosure__copy">
                            <strong>Estimated {{ formatCurrency(estimatedTotal) }} to align these licenses.</strong>
                            That is the total of the estimated extensions above. Each license is priced again
                            when you schedule, so the amount is whatever the invoice for this alignment says —
                            this screen reports what it came to, and whether it was collected, as soon as one is
                            raised. All of these licenses then renew together on
                            {{ formatBillingDate(unifiedRenewalDate) }}.
                        </div>
                    </div>

                    <div v-if="!hasPaymentMethod && !nothingToCharge" class="payment-warning">
                        <PhWarning :size="18" class="payment-warning__icon" />
                        <span>
                            No payment method on file. An invoice raised for this alignment posts on your account
                            terms rather than being charged — you'll need a payment method to pay it in-app.
                        </span>
                    </div>

                    <PrimeButton
                        label="Schedule consolidation"
                        class="confirm-btn"
                        :loading="scheduling"
                        :disabled="scheduling || isLoading || preview.blocks.length === 0 || nothingToCharge"
                        @click="handleSchedule"
                    />
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { PhX, PhInfo, PhWarning, PhCheckCircle } from '@phosphor-icons/vue';
import { formatCurrency } from '@privacy-bee/format/currency';
// Explicit: auto-import would expect `BusinessSharedLicenseBadge` here, and
// `<LicenseBadge>` would render an empty element.
import LicenseBadge from '~/components/business/shared/LicenseBadge.vue';
import { businessService } from '~/services/business-service';
import { formatBillingDate } from '~/utils/billing-date';
import { ApiError, memberFacingMessage } from '~/utils/errors';
import { ModalNames } from '~/types/modals';
import { getDialogRef } from '~/composables/dynamic-dialog';
import type { ConsolidationPreview, ConsolidationScheduleSummary } from '~/types/consolidation';

/** app-api's refusal of a schedule that lost the race: the company already has an active consolidation. */
const CONSOLIDATION_ACTIVE = 'consolidation_active';

const dialogRef = getDialogRef(ModalNames.CONSOLIDATE_BILLING_DRAWER);
const { showError } = useGlobalToast();

const data = computed(() => dialogRef.value?.data);
const hasPaymentMethod = computed(() => data.value?.hasPaymentMethod ?? true);

const preview = ref<ConsolidationPreview | null>(null);
const earliestSharedDate = ref<string | null>(null);
const sharedDate = ref<string>('');
const isLoading = ref(true);
const scheduling = ref(false);
const scheduledSummary = ref<ConsolidationScheduleSummary | null>(null);

// What the administrator is about to pay, stated before the press (PB-074).
//
// A sum of the server's own per-block figures and nothing else — no price is derived
// here. An exempt block quotes 0, so summing every block is the same figure as summing
// the chargeable ones, and one reduce serves both this total and the refusal below.
const estimatedTotal = computed(() =>
    (preview.value?.blocks ?? []).reduce((sum, b) => sum + b.estimated_extension_amount, 0),
);

// A consolidation is never free: app-api refuses one that would cost nothing with a 422
// (CreateConsolidationInvoiceAction::chargeableBlocks). The preview still returns the
// exempt blocks at 0 rather than refusing, so the refusal is predicted from those figures
// and the press is disabled with the reason — a Schedule that can only fail is worse than
// no Schedule at all.
const nothingToCharge = computed(() => (preview.value?.blocks.length ?? 0) > 0 && estimatedTotal.value === 0);

const unifiedRenewalDate = computed(() => preview.value?.shared_date ?? sharedDate.value);

// The invoice the schedule response reports, or null on an API that predates PB-074 and
// on a schedule whose extensions all landed on invoices that already existed. `== null`
// covers absent and null together: both render the pre-PB-074 confirmation.
const scheduledInvoice = computed(() => scheduledSummary.value?.invoice ?? null);
const chargedNow = computed(() => scheduledInvoice.value?.status === 'paid');

// Settled out of account credit rather than collected. `amount` is NOT the gross invoice
// here, nor on the charged branch above: applyOpenCredits() writes the draw as a negative
// `credit_applied` item and re-runs calculateTotal() before saving, so the stored amount is
// already net of credit and is what the card was asked for. Credit covering the whole
// alignment takes it to zero, and app-api closes the invoice rather than charging zero.
const creditSettled = computed(() => scheduledInvoice.value?.status === 'closed');

const hasMixedTerms = computed(() => {
    const terms = new Set((preview.value?.seats ?? []).map((s) => s.term));
    return terms.size > 1;
});

// Monotonic request id: if the admin changes the shared date several times, an
// older preview can resolve last and clobber the newer selection (Codex H5). Only
// the most-recently-issued request is allowed to apply its result.
let previewSeq = 0;

async function loadPreview(date?: string) {
    if (!data.value) return;
    const seq = ++previewSeq;
    isLoading.value = true;
    try {
        const result = await businessService.previewConsolidation(data.value.companyId, date);
        if (seq !== previewSeq) return; // a newer request superseded this one
        // `blocks` is what the schedule renders and what gates Schedule, so an API
        // that predates it degrades to an empty, unschedulable drawer rather than a
        // render error (this ships after the endpoint, but not simultaneously).
        preview.value = { ...result, blocks: result.blocks ?? [] };
        if (result.shared_date) {
            sharedDate.value = result.shared_date;
            // The first (un-dated) preview defaults to the furthest-out renewal —
            // that is the earliest a shared date may be (D1).
            if (!earliestSharedDate.value) earliestSharedDate.value = result.shared_date;
        }
    } catch (err: unknown) {
        if (seq !== previewSeq) return;
        showError('Error', memberFacingMessage(err, 'Failed to load consolidation preview'));
        preview.value = null;
    } finally {
        if (seq === previewSeq) isLoading.value = false;
    }
}

function onSharedDateChange(event: Event) {
    const next = (event.target as HTMLInputElement).value;
    if (!next) return;
    if (earliestSharedDate.value && next < earliestSharedDate.value) {
        // Snap back — the backend is the authoritative rejection, this is just UX.
        (event.target as HTMLInputElement).value = sharedDate.value;
        return;
    }
    sharedDate.value = next;
    loadPreview(next);
}

async function handleSchedule() {
    if (!data.value || scheduling.value) return;
    scheduling.value = true;
    try {
        scheduledSummary.value = await businessService.scheduleConsolidation(data.value.companyId, sharedDate.value);
        const onScheduled = data.value.onScheduled;
        if (typeof onScheduled === 'function') onScheduled();
    } catch (err: unknown) {
        showError('Error', memberFacingMessage(err, 'Failed to schedule consolidation'));

        // Another administrator scheduled first. Only one consolidation can be
        // active, so re-offering Schedule here can never succeed: reload the page
        // behind the drawer and get out of the way so the administrator sees the
        // consolidation that won. Every other refusal, 409s among them (a payment
        // still settling on one of these licenses, another payment holding the
        // company's payer lock), keeps the drawer open: its sentence says what to
        // wait for, and Schedule can succeed once it has passed.
        if (err instanceof ApiError && err.reason === CONSOLIDATION_ACTIVE) {
            const onScheduled = data.value?.onScheduled;
            if (typeof onScheduled === 'function') onScheduled();
            scheduling.value = false;
            dialogRef.value?.close();
        }
    } finally {
        scheduling.value = false;
    }
}

function closeDrawer() {
    // Scheduling is still rewriting every license's renewal date server-side —
    // keep the drawer open (backdrop clicks route here too) so the admin sees
    // the confirmation or error instead of a silently applied consolidation.
    if (scheduling.value) return;
    dialogRef.value?.close();
}

// The opener hears a schedule while it is out (`onWriting`): its unmount would close this drawer
// past the refusal above, and the administrator would never see the confirmation or the error.
// The teardown releases it.
watch(scheduling, (writing) => data.value?.onWriting?.(writing));
onBeforeUnmount(() => data.value?.onWriting?.(false));

onMounted(() => loadPreview());
</script>

<style scoped lang="scss">
.drawer-backdrop {
    @apply fixed inset-0 bg-black/40 z-50;
}

.block-drawer {
    @apply bg-white w-full sm:w-[520px] h-screen flex flex-col overflow-y-auto right-0 fixed top-0;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.08);
}

.drawer-header {
    @apply flex justify-between items-start px-6 py-5 border-b border-zinc-200 flex-shrink-0;

    .header-content {
        @apply flex-1 min-w-0 pr-4;

        .block-name {
            @apply text-xl font-bold text-zinc-900 m-0 mb-1 truncate;
        }

        .block-meta {
            @apply text-sm text-zinc-500 m-0;
        }
    }

    .close-btn {
        @apply w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors flex-shrink-0;
        @apply disabled:opacity-40 disabled:pointer-events-none;
    }
}

.drawer-loading,
.drawer-empty {
    @apply p-6;
}

.drawer-empty {
    @apply text-sm text-zinc-500;
}

.drawer-body {
    @apply flex-1 overflow-y-auto px-6 py-6;
}

.drawer-section {
    @apply mb-6;

    .section-head {
        @apply flex items-baseline justify-between mb-3;

        .section-title {
            @apply text-xs font-semibold text-zinc-500 uppercase tracking-wider m-0;
        }

        .section-count {
            @apply text-xs text-zinc-400;
        }
    }

    .section-lede {
        @apply text-xs text-zinc-500 leading-relaxed mt-0 mb-3;
    }
}

.field-label {
    @apply block text-sm font-medium text-zinc-900 mb-2;
}

.date-input {
    @apply w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40;
}

.field-hint {
    @apply text-xs text-zinc-500 mt-2 mb-0;
}

.seat-list {
    @apply m-0 p-0 list-none flex flex-col gap-2;
}

.seat-row {
    @apply flex items-center justify-between px-4 py-3 rounded-lg border border-zinc-200 bg-white gap-3;

    .seat-meta {
        @apply flex flex-col items-start min-w-0;

        .seat-person {
            @apply text-sm font-medium text-zinc-900 truncate;
        }

        // Same treatment the financial page's block table gives the id, so the two
        // screens read as the same identifier rather than two coincidences.
        .block-id {
            @apply text-xs text-zinc-400 font-mono mt-1;
        }

        .seat-detail {
            @apply text-xs text-zinc-500 mt-0.5;
        }
    }

    .seat-amount {
        @apply text-right flex-shrink-0;

        .seat-exempt {
            @apply text-xs text-zinc-500;
        }

        .seat-est {
            @apply text-sm font-semibold text-zinc-900;

            &__label {
                @apply block text-xs font-normal text-zinc-400;
            }
        }
    }
}

.following-renewal {
    @apply border-dashed bg-zinc-50/60;
}

.drawer-help {
    @apply text-xs text-zinc-500 leading-relaxed mt-4 mb-0;
}

.drawer-footer {
    @apply px-6 py-5 border-t border-zinc-200 bg-zinc-50/60 flex-shrink-0 flex flex-col gap-3;
}

.disclosure {
    @apply flex gap-2 items-start text-sm text-zinc-700;

    &__icon {
        @apply text-blue-500 flex-shrink-0 mt-0.5;
    }
}

.payment-warning {
    @apply flex gap-2 items-start text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2;

    &__icon {
        @apply text-amber-500 flex-shrink-0 mt-0.5;
    }
}

.confirm-btn {
    @apply w-full;
}

.scheduled-confirm {
    @apply flex flex-col items-center text-center py-8;

    &__icon {
        @apply text-green-500 mb-3;
    }

    &__title {
        @apply text-lg font-bold text-zinc-900 m-0 mb-2;
    }

    &__line {
        @apply text-sm text-zinc-600 m-0 mb-1;
    }

    // The money line is the one an administrator is looking for on this screen.
    &__money {
        @apply text-zinc-900 font-semibold;
    }
}

.slide-in-right-enter-active,
.slide-in-right-leave-active {
    transition: transform 0.25s ease;
}

.slide-in-right-enter-from,
.slide-in-right-leave-to {
    transform: translateX(100%);
}
</style>
